import type { ComponentType } from 'react';

import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded';
import type { SvgIconProps } from '@mui/material/SvgIcon';

import type { AccentName } from '@/theme';

export type PostType = 'GOOD' | 'BAD' | 'IDEAL';
export type PostScope = 'TOWN' | 'COUNTRY' | 'LIFE';

export interface RegisterDefinition {
  type: PostType;
  /** Palette key — also the MUI `color` prop for Button/Chip. */
  accent: AccentName;
  Icon: ComponentType<SvgIconProps>;
}

/**
 * The single source of truth for the three registers.
 *
 * Spec §9.2 requires that a register is never signalled by colour alone, so
 * every definition carries an icon alongside its accent, and every component
 * that renders a register renders the icon, the accent *and* the label.
 *
 * The labels themselves live in the locale files under `registers.<TYPE>` —
 * a module-level constant cannot re-render when the language changes.
 */
export const REGISTERS: Record<PostType, RegisterDefinition> = {
  GOOD: { type: 'GOOD', accent: 'good', Icon: WbSunnyRoundedIcon },
  BAD: { type: 'BAD', accent: 'bad', Icon: ReportProblemRoundedIcon },
  IDEAL: { type: 'IDEAL', accent: 'ideal', Icon: AutoAwesomeRoundedIcon },
};

export const REGISTER_ORDER: PostType[] = ['GOOD', 'BAD', 'IDEAL'];

export interface ScopeDefinition {
  scope: PostScope;
  Icon: ComponentType<SvgIconProps>;
}

/** Labels live under `scopes.<SCOPE>` in the locale files, for the same reason. */
export const SCOPES: Record<PostScope, ScopeDefinition> = {
  TOWN: { scope: 'TOWN', Icon: HomeWorkRoundedIcon },
  COUNTRY: { scope: 'COUNTRY', Icon: PublicRoundedIcon },
  LIFE: { scope: 'LIFE', Icon: PersonRoundedIcon },
};

export const SCOPE_ORDER: PostScope[] = ['TOWN', 'COUNTRY', 'LIFE'];

/** Regional-indicator pair for an ISO 3166-1 alpha-2 code, e.g. RO -> 🇷🇴. */
export function flagEmoji(countryCode: string): string {
  if (!/^[A-Za-z]{2}$/.test(countryCode)) return '🏳️';

  const base = 0x1f1e6;
  return [...countryCode.toUpperCase()]
    .map((char) => String.fromCodePoint(base + char.charCodeAt(0) - 'A'.charCodeAt(0)))
    .join('');
}
